import { CheckCircle, ExclamationCircle, ExclamationTriangle, XMark } from '@ads/icons';
import { cva, type VariantProps } from 'class-variance-authority';
import { toast as sonner } from 'sonner';

type IconProps = VariantProps<typeof statusIcon>;

const statusIcons: Record<NonNullable<IconProps['status']>, typeof CheckCircle> = {
  success: CheckCircle,
  warning: ExclamationTriangle,
  danger: ExclamationCircle,
};

export type ToastProps = IconProps & {
  title: string;
  description?: string | null;
  action?: {
    label: string;
    callback: () => Promise<unknown> | unknown;
  };
};

export const toast = ({ status, title, description, action }: ToastProps) => {
  sonner.custom((t) => {
    const StatusIcon = statusIcons[status ?? 'success'];
    return (
      <div className='flex gap-mini relative bg-ctx-layout-spotlight p-mini rounded-sm border border-ctx-layout-body shadow-level4 data-[state=open]:animate-slideIn data-[state=closed]:animate-slideOut data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=cancel]:translate-x-0 data-[swipe=cancel]:transition-[transform_200ms_ease-out] data-[swipe=end]:animate-swipeOut w-[calc(100%-2rem)] sm:w-auto'>
        <button
          onClick={() => sonner.dismiss(t)}
          type='button'
          aria-label='Fechar alerta'
          className='absolute top-micro right-micro w-xxxs h-xxxs'
        >
          <XMark aria-hidden className='text-ctx-content-base w-5 h-5' />
        </button>
        <div>
          <div className={statusIcon({ status })}>
            <StatusIcon aria-hidden className='w-xxxs h-xxxs mx-auto' />
          </div>
        </div>
        <div>
          <p className='font-medium text-ctx-content-title mb-quark'>{title}</p>
          {description && <p className='text-xxs text-ctx-content-base/semiOpaque'>{description}</p>}
          {!!action && (
            <div className='mt-mini space-x-micro text-xxs text-ctx-content-base/semiOpaque'>
              <button type='button' onClick={() => sonner.dismiss(t)}>
                Cancelar
              </button>
              <button
                onClick={async () => {
                  const actionButton = document.querySelector('#linka-action-button');
                  if (actionButton) actionButton.textContent = 'Carregando...';
                  await action.callback();
                  sonner.dismiss(t);
                }}
                type='button'
                id='linka-action-button'
                className='text-ctx-content-button'
              >
                {action.label}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  });
};

const statusIcon = cva('shrink-none rounded-pill w-sm h-sm content-center', {
  variants: {
    status: {
      success: 'text-base-success-600 bg-base-success-100',
      warning: 'text-base-warning-600 bg-base-warning-100',
      danger: 'text-base-danger-600 bg-base-danger-100',
    },
  },
  defaultVariants: {
    status: 'success',
  },
});
